import { SellOrderDetailedApiResponse } from 'utils/sell-process/sell-process-detailed-types';
import { FiscalYear, RelationType } from '../../../global/entities/BuyOrder';
import { Settelment } from '../../../global/entities/SellOrder';
import { AuditorOptionsType } from '../../buy-process-form/components/auditor/auditor-types';
import { SellRepresentative } from './components/sell-process-form/components/representatives/representatives-types';
import { ShellCompanyDeclarationFormState } from './components/sell-process-form/components/shell-company-declaration/shell-company-declaration-types';

export type SellOrder = {
  id: string;

  businessManager: { id: string; userId: string };
  liquidator: { id: string; userId: string };
  adjuster: { id: string; userId: string };
  signer: { id: string; userId: string };
  taxOfficeContactPerson: { id: string; userId: string };

  shareCapital: number;
  dateOffer: string;
  dateAgreement: string;
  numberShares: number;
  purchasePrice: number;
  contractWording?: string;
  equity: number;
  acquiredCash: number;
  tcNumber?: number;
  commision: boolean;
  commisionAmount: number;
  settelmentType: Settelment;
  dateDecided: string;
  dateSent: string;
  datePurchased: string;
  fiscalYear: FiscalYear;

  changedFiscalYear: boolean;

  fiscalStartDate?: string;
  fiscalEndDate?: string;
  banks: SellOrderBank[];
  isAnotherBuyer: boolean;
  anotherBuyer?: {
    name: string;
    organizationNumber: string;
  };
  company: SellOrderCompanyStore;
  physicalPeople: SellOrderPhysicalShareholderStore[];
  legalShareholders: SellOrderLegalShareholderStore[];
  representatives: SellOrderRepresentativeStore[];
  auditor: SellOrderAuditorStore;
  bankAccount?: string;
  clientFundsAccount?: string;
  bank?: string;
  auditorReservation: boolean;
  AGAregistered?: boolean;
  VATregistered?: boolean;
  AGAcreatedFrom?: string;
  VATcreatedFrom?: string;
  dateOfRevocationFTax?: string;
  parentOrderId?: string;
  hasChildOrder?: boolean;
  childOrdersDetails?: SellOrderDetailedApiResponse[];
  deleteRequest?: SellProcessDeleteRequest[];
  shellCompanyDeclaration?: ShellCompanyDeclarationStore;
};

export type ShellCompanyDeclarationStore = ShellCompanyDeclarationFormState;

export type SellOrderBank = {
  id: string;
  bankId: string;
  bankAccount?: string;
  bankType: BankKind;
};

export type SellOrderRepresentative = {
  relationType: RelationType;
  firstName: string;
  lastName: string;
  socialSecurityNumber: string;
  city: string;
  address: string;
  postalCode: string;
  personId?: string;
  relationId?: string;
};

export type SellOrderRepresentativeStore = Pick<
  SellOrderRepresentative,
  'relationId' | ('relationType' & Pick<SellRepresentative, 'signer'>)
> & {
  person: string;
};

export type SellOrderAuditorStore = {
  id?: string;
  auditorOption: AuditorOptionsType;
  auditorPerson: {
    firstName: string;
    lastName: string;
    socialSecurityNumber: string;
    city: string;
    address: string;
    postalCode: string;
  };
  auditorFirm: {
    organizationNumber?: string;
    organizationName?: string;
    organizationAddress?: string;
    organizationPostalCode?: string;
    organizationCity?: string;
    organizationSigner: boolean;
  };
};

export type SellOrderLegalShareholder = {
  organizationAddress: string;
  organizationCity: string;
  organizationName: string;
  organizationNumber: string;
  organizationPostalCode: string;
  numberShares: number;
  relationType: RelationType;
  bankId: string;
  bankAccount: string;
  signers: [
    {
      person: SellOrderSignerStore;
      signerId?: string;
    }
  ];
};

export type SellOrderLegalShareholderStore = Pick<
  SellOrderLegalShareholder,
  'relationType' | 'numberShares'
> & {
  organizationId?: string;
};

export type SellOrderSignerStore = {
  firstName: string;
  lastName: string;
  socialSecurityNumber: string;
};

export type SellOrderPhysicalShareholder = {
  numberShares: number | null;
  relationType: RelationType;
  firstName: string;
  lastName: string;
  socialSecurityNumber: string;
  city: string;
  address: string;
  postalCode: string;
  bankId: string;
  bankAccount: string;
};

export type SellOrderPhysicalShareholderStore = Pick<
  SellOrderPhysicalShareholder,
  'numberShares' | 'relationType'
> & {
  relationId?: string;
  city: string;
  person: string;
};

export type SellOrderCompanyStore = {
  name: string;
  number: string;
  address: string;
  city: string;
  postalCode: string;
  lowestShareCapital: number;
  highestShareCapital: number;
  lowestNumberShares: number;
  highestNumberShares: number;
};

export enum SellOrderStatus {
  Free,
  AllInformationCollected,
  AgreementAndPowerAttorneySent,
  SignedAgreement,
  ReadyForPR,
  PurchasePricePaid,
  BankClosingSent,
  BankClosingPaid,
  SwedishCompaniesRegistrationOfficePaid,
  BVSent,
  BVRegistered,
  LequidationSettlement,
  Clear,
}

export enum SellOrderRelationType {
  InternalManager = 'internalManager',
  Customer = 'customer',
  Signer = 'signer',
  Adjuster = 'adjuster',
  Liquidator = 'liquidator',
  BusinessManager = 'businessManager',
  TaxOfficeContactPerson = 'taxOfficeContactPerson',
}

export enum BankKind {
  Internal = 0,
  Company,
  Order,
}

export type SellProcessDeleteRequest = {
  signerId?: string;
  organizationId?: string;
  relationId?: string;
};
